<template>
  <closeable-dialog
    :retain-focus="false"
    :hash="'gallery_' + id"
    v-model="dialog"
  >
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" :on="on" :attrs="attrs" />
    </template>
    <template v-slot:header>
      <slot name="header"></slot>
    </template>

    <GalleryViewPreview
      v-if="!hidePreview"
      :previewed-gallery-media="previewedGalleryMedia"
      :key="'preview' + previewKey"
    />
    <GalleryViewGrid
      :style="{
        flexBasis: hidePreview ? '100%' : '60%',
      }"
      :compact="isMobile"
      :load-gallery-media="loadGalleryMedia"
      :total-gallery-media="totalGalleryMedia"
      :sortable="sortable"
      @mediumSelected="previewedGalleryMedia = $event"
      @reorder="$emit('reorder', $event)"
      :key="'grid' + gridKey"
      :optimistic-update="optimisticUpdate"
      :is-highlight-gallery="isHighlightGallery"
    />
  </closeable-dialog>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  ref,
  WritableComputedRef,
} from "vue"
import { useIsBreakpointInNames } from "features/common/vuetify/useVuetify"
import GalleryViewPreview from "features/gallery/view/GalleryViewPreview.vue"
import GalleryViewGrid from "features/gallery/view/GalleryViewGrid.vue"
import CloseButton from "features/common/ui/CloseButton.vue"
import {
  GalleryMedium,
  IndexedGalleryMedium,
} from "features/gallery/gallery.types"
import { EventBus } from "core/event-bus/event-bus"
import CloseableDialog from "components/UI/CloseableDialog.vue"

export type LoadGalleryMediaFunction = (
  offset: number,
  limit: number
) => Promise<GalleryMedium[]>

export default defineComponent({
  name: "GalleryView",
  components: {
    CloseableDialog,
    CloseButton,
    GalleryViewGrid,
    GalleryViewPreview,
  },
  props: {
    loadGalleryMedia: {
      type: Function as PropType<LoadGalleryMediaFunction>,
    },
    totalGalleryMedia: {
      type: Number,
      default: 0,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    optimisticUpdate: {
      type: Boolean,
      default: false,
    },
    openEvent: {
      type: Object as PropType<EventBus<boolean>>,
      default: null,
    },
    id: {
      type: String,
      required: true,
    },
    isHighlightGallery: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const previewedGalleryMedia = ref<IndexedGalleryMedium | null>(null)
    if (props.openEvent) {
      props.openEvent.on((value) => {
        dialog.value = value
      })
    }

    const isMobile = useIsBreakpointInNames(["xs", "sm"])
    const isTabletLandscape = useIsBreakpointInNames(["md"])

    const hidePreview = computed(
      () => isMobile.value || isTabletLandscape.value
    )

    const previewKey = ref(0)
    const gridKey = ref(0)

    const _dialog = ref(false)

    const dialog: WritableComputedRef<boolean> = computed({
      get() {
        return _dialog.value
      },
      set(value) {
        _dialog.value = value
        if (props.optimisticUpdate) {
          previewKey.value++
          gridKey.value++
        }
      },
    })

    return {
      previewedGalleryMedia,
      hidePreview,
      isMobile,
      dialog,
      previewKey,
      gridKey,
      id: props.id,
    }
  },
})
</script>
